import { readSaved, save } from './storage.js';

// One cabinet interface; the individual games retain their own simulations.
export const arcade = {
  paused: false,
  muted: readSaved('arcade-muted') === 'true',
  get soundEnabled() { return !this.muted && !this.paused; },
  mount({ title, help, active, clearInput, restart, retryLevel = null, onResume = () => {} }) {
    const canvas = document.querySelector('canvas');
    canvas.tabIndex = 0;
    canvas.setAttribute('aria-label', `${title} playfield. ${help}`);
    canvas.textContent = `${title} needs a browser with canvas support.`;
    const bar = document.createElement('nav');
    bar.className = 'arcade-toolbar';
    bar.setAttribute('aria-label', 'Arcade controls');
    bar.innerHTML = `<a href="../index.html#arcade">‹ Arcade lobby</a>
      <div class="arcade-tools"><button type="button" id="arcade-pause">Pause (P)</button>
      <button type="button" id="arcade-sound" aria-pressed="false">Sound</button>
      <button type="button" id="arcade-fullscreen">Full screen</button>
      <button type="button" id="arcade-help">How to play</button></div>`;
    document.body.prepend(bar);
    const status = document.createElement('div');
    status.className = 'arcade-session';
    status.innerHTML = '<p id="arcade-status"></p><progress id="arcade-progress" max="100" value="0" aria-label="Game progress"></progress>';
    canvas.after(status);
    const dialog = document.createElement('dialog');
    dialog.className = 'arcade-dialog';
    dialog.setAttribute('aria-labelledby', 'arcade-dialog-title');
    dialog.innerHTML = `<h2 id="arcade-dialog-title">Take a breather.</h2>
      <p id="arcade-dialog-copy"></p><p>${help}</p>
      <div class="arcade-dialog-actions"><button type="button" id="arcade-resume">Resume game</button>
      <button type="button" id="arcade-retry" hidden>Retry this level</button>
      <button type="button" id="arcade-restart">Start a new game</button>
      <a href="../index.html#arcade">Back to arcade</a></div>`;
    document.body.append(dialog);
    const pauseButton = bar.querySelector('#arcade-pause');
    const soundButton = bar.querySelector('#arcade-sound');
    const resumeButton = dialog.querySelector('#arcade-resume');
    const restartButton = dialog.querySelector('#arcade-restart');
    const retryButton = dialog.querySelector('#arcade-retry');
    const syncSound = () => {
      soundButton.textContent = this.muted ? 'Sound off (M)' : 'Sound on (M)';
      soundButton.setAttribute('aria-pressed', String(this.muted));
      soundButton.setAttribute('aria-label', this.muted ? 'Unmute sound' : 'Mute sound');
    };
    const resume = () => {
      dialog.close();
      this.paused = false;
      clearInput();
      onResume();
      pauseButton.textContent = 'Pause (P)';
      canvas.focus({ preventScroll: true });
    };
    const pause = (helpOnly = false) => {
      if (dialog.open || (!active() && !helpOnly)) return;
      this.paused = true;
      clearInput();
      document.querySelectorAll('#mobileControls .active').forEach(el => el.classList.remove('active'));
      pauseButton.textContent = 'Resume (P)';
      dialog.querySelector('#arcade-dialog-title').textContent = helpOnly ? `${title}: how to play` : 'Take a breather.';
      dialog.querySelector('#arcade-dialog-copy').textContent = active() ? 'Your game is paused. Your score is safe.' : 'Free play. No quarters required.';
      resumeButton.textContent = active() ? 'Resume game' : 'Back to game';
      restartButton.hidden = !active();
      retryButton.hidden = !retryLevel || !active();
      dialog.showModal();
      resumeButton.focus();
    };
    pauseButton.onclick = () => this.paused ? resume() : pause();
    bar.querySelector('#arcade-help').onclick = () => pause(true);
    resumeButton.onclick = resume;
    dialog.addEventListener('cancel', e => { e.preventDefault(); resume(); });
    retryButton.onclick = () => { resume(); retryLevel?.(); };
    restartButton.onclick = () => { resume(); restart(); canvas.focus({ preventScroll: true }); };
    soundButton.onclick = () => { this.muted = !this.muted; save('arcade-muted', this.muted); syncSound(); canvas.focus({ preventScroll: true }); };
    const fullscreen = bar.querySelector('#arcade-fullscreen');
    fullscreen.hidden = !document.fullscreenEnabled;
    fullscreen.onclick = async () => {
      try {
        if (document.fullscreenElement) await document.exitFullscreen();
        else await document.documentElement.requestFullscreen();
      } catch { fullscreen.textContent = 'Full screen unavailable'; }
    };
    document.addEventListener('fullscreenchange', () => { fullscreen.textContent = document.fullscreenElement ? 'Exit full screen' : 'Full screen'; });
    // Capture shortcuts before each game's own handlers. Native controls retain Space/Enter.
    document.addEventListener('keydown', e => {
      const control = e.target.closest?.('button, a, input, select, textarea, summary');
      const typing = e.target.closest?.('input, select, textarea');
      if (!typing && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (e.code === 'KeyP' || e.code === 'Escape') {
          e.preventDefault(); e.stopImmediatePropagation();
          if (!e.repeat) this.paused ? resume() : pause();
          return;
        }
        if (e.code === 'KeyM') {
          e.preventDefault(); e.stopImmediatePropagation();
          if (!e.repeat) soundButton.click();
          return;
        }
      }
      if (this.paused || control) e.stopImmediatePropagation();
    }, true);
    window.addEventListener('blur', () => { clearInput(); pause(); });
    document.addEventListener('visibilitychange', () => { if (document.hidden) { clearInput(); pause(); } });
    // Move keyboard focus from a start/continue button onto the running game.
    document.getElementById('gameContainer').addEventListener('click', e => {
      if (e.target.closest('button') && active()) canvas.focus({ preventScroll: true });
    });
    syncSound();
    this.status = (text, progress = 0) => {
      const label = status.querySelector('p');
      if (label.textContent !== text) label.textContent = text;
      status.querySelector('progress').value = Math.max(0, Math.min(100, progress));
      pauseButton.disabled = !active();
    };
  },
  status() {},
};

// Pointer capture keeps holds reliable when a thumb slides off a control.
export function bindPointer(button, press, release = () => {}) {
  let pointer = null;
  button.addEventListener('pointerdown', e => {
    if (arcade.paused || pointer !== null || (e.pointerType === 'mouse' && e.button !== 0)) return;
    e.preventDefault();
    pointer = e.pointerId;
    button.setPointerCapture(pointer);
    button.classList.add('active');
    press();
  });
  const end = e => {
    if (pointer !== e.pointerId) return;
    pointer = null;
    release();
    button.classList.remove('active');
  };
  button.addEventListener('pointerup', end);
  button.addEventListener('pointercancel', end);
  button.addEventListener('lostpointercapture', end);
}
